import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  Paper,
  Stack,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  FormControl,
  Fade,
  Slide,
} from '@mui/material';
import {
  Add,
  Delete,
  AttachMoney,
  Schedule,
  Star,
  Info,
} from '@mui/icons-material';
import { RootState } from '../../../store';
import { handleError } from '../../../utils/config';

// Data type
export interface PlanDetailsProps {
  id?: number;
  name: string;
  description: string;
  pricings: { price: number; duration: 'monthly' | 'yearly' }[];
  features: {
    id?: number;
    name: string;
    description?: string;
    max_limit: number;
  }[];
}

interface PlanFormDialogProps {
  open: boolean;
  initialData?: PlanDetailsProps | undefined;
  onClose: () => void;
  onSubmit: any;
  deleteFeature?: any;
}

export const PlanFormDialog: React.FC<PlanFormDialogProps> = ({
  open,
  initialData,
  onClose,
  onSubmit,
  deleteFeature,
}) => {
  const [formError, setFormError] = useState<any>({});
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [pricings, setPricings] = useState<
    { price: number; duration: 'monthly' | 'yearly' }[]
  >([]);
  const [features, setFeatures] = useState<
    { id?: number; name: string; description?: string; max_limit: number }[]
  >([]);
  const [deleteFeatureIds, setDeleteFeatureIds] = useState<any>([]);

  // Load initial data if editing
  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setDescription(initialData.description);
      setPricings(initialData.pricings || []);
      setFeatures(initialData.features || []);
    } else {
      setName('');
      setDescription('');
      setPricings([]);
      setFeatures([]);
      setFormError({});
    }
  }, [initialData, open]);

  const validateForm = ({ name, pricings, features }: any) => {
    let isValid = true;
    if (!name) {
      setFormError((prv: RootState) => {
        return { ...prv, name: 'Plan Name should not be empty' };
      });
      isValid = false;
    }

    if (!pricings?.length) {
      setFormError((prv: RootState) => ({
        ...prv,
        pricings: 'At least one pricing option is required',
      }));
      isValid = false;
    }
    if (
      pricings.some(
        (p: { price: number; duration: string }) =>
          !p.price ||
          p.price <= 0 ||
          !['monthly', 'yearly'].includes(p.duration)
      )
    ) {
      setFormError((prv: RootState) => ({
        ...prv,
        pricings: 'Each pricing must have a valid price and duration',
      }));
      isValid = false;
    }

    if (!features.length) {
      setFormError((prv: RootState) => ({
        ...prv,
        features: 'At least one feature is required',
      }));
      isValid = false;
    }
    if (
      features.some(
        (f: { name: string; max_limit: number }) =>
          !f.name ||
          f.name.trim() === '' ||
          f.max_limit === undefined ||
          f.max_limit < 0
      )
    ) {
      setFormError((prv: RootState) => ({
        ...prv,
        features: 'Each feature must have a name and a non-negative max limit',
      }));
      isValid = false;
    }
    return isValid;
  };

  const handleSubmit = async () => {
    try {
      setFormError({});
      const isValid = validateForm({ name, pricings, features });
      if (isValid) await onSubmit({ name, description, pricings, features });
      if (initialData && deleteFeatureIds?.length > 0)
        await deleteFeature({
          planId: initialData?.id,
          featureIds: deleteFeatureIds,
        });
    } catch (error) {}
  };

  const addPricing = () =>
    setPricings([...pricings, { price: 0, duration: 'monthly' }]);
  const updatePricing = (
    index: number,
    field: 'price' | 'duration',
    value: any
  ) => {
    const updated = [...pricings];
    updated[index] = { ...updated[index], [field]: value };
    setPricings(updated);
  };

  const addFeature = () =>
    setFeatures([...features, { name: '', description: '', max_limit: 0 }]);
  const removeFeature = (index: number | undefined) => {
    setFeatures(features.filter(({ id }: any) => id !== index));
    setDeleteFeatureIds([...deleteFeatureIds, index]);
  };
  const updateFeature = (
    index: number,
    field: 'name' | 'description' | 'max_limit',
    value: any
  ) => {
    const updated = [...features];
    updated[index] = { ...updated[index], [field]: value };
    setFeatures(updated);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow:
            '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        },
      }}
      TransitionComponent={Slide}
      // TransitionProps={{ direction: 'up' }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          pb: 3,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {initialData ? 'Edit Plan' : 'Create New Plan'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {initialData
                ? 'Update your existing plan details'
                : 'Set up pricing and features for your new plan'}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                transform: 'rotate(90deg)',
                transition: 'all 0.3s ease',
              },
            }}
          >
            <Add sx={{ transform: 'rotate(45deg)' }} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          <Stack spacing={4}>
            {/* Plan Details Section */}
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack spacing={3}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Info color="primary" />
                    <Typography variant="h4" fontWeight="600">
                      Plan Details
                    </Typography>
                  </Box>

                  <Stack spacing={2.5}>
                    <TextField
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      fullWidth
                      variant="outlined"
                      required={true}
                      error={!!formError?.name}
                      helperText={
                        !!formError?.name ? formError?.name.trim() : ''
                      }
                      placeholder="Enter the Plan Name e.g., Premium Plan, Starter Package"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          fontSize: '1.5rem',
                          '&:hover': {
                            '& > fieldset': { borderColor: 'primary.main' },
                          },
                        },
                      }}
                    />

                    <TextField
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      placeholder="Describe what this plan offers to your customers..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          fontSize: '1.5rem',
                          '&:hover': {
                            '& > fieldset': { borderColor: 'primary.main' },
                          },
                        },
                      }}
                    />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            {/* Pricing Section */}
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack spacing={3}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      width={'100%'}
                    >
                      <AttachMoney color="success" />
                      <Typography variant="h4" fontWeight="600">
                        Pricing Options
                      </Typography>
                      <Chip size="small" color="success" variant="outlined" />
                    </Box>
                    <Button
                      startIcon={<Add />}
                      onClick={addPricing}
                      variant="contained"
                      size="small"
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '1.5rem',
                        background:
                          'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)',
                        '&:hover': {
                          background:
                            'linear-gradient(45deg, #43a047 30%, #4caf50 90%)',
                        },
                      }}
                    >
                      Add
                    </Button>
                  </Stack>

                  {pricings.length === 0 ? (
                    <Paper
                      sx={{
                        p: 6,
                        textAlign: 'center',
                        bgcolor: 'grey.50',
                        border: '2px dashed',
                        borderColor: 'grey.300',
                        borderRadius: 2,
                      }}
                    >
                      <AttachMoney
                        sx={{ fontSize: 48, color: 'grey.400', mb: 2 }}
                      />
                      <Typography variant="body1" color="text.secondary">
                        No pricing options yet. Click "Add Pricing" to get
                        started.
                      </Typography>
                      {!!formError?.pricings && (
                        <Typography variant="body2" color={'error'}>
                          *{formError?.pricings}
                        </Typography>
                      )}
                    </Paper>
                  ) : (
                    <Stack spacing={2}>
                      {pricings.map((pricing, index) => (
                        <Fade in key={index}>
                          <Paper
                            elevation={1}
                            sx={{
                              p: 3,
                              borderRadius: 2,
                              background:
                                'linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%)',
                              border: '1px solid',
                              borderColor: 'success.light',
                            }}
                          >
                            <Stack direction="row" spacing={2} alignItems="end">
                              <TextField
                                type="text"
                                value={pricing.price || ''}
                                onChange={(e) =>
                                  updatePricing(
                                    index,
                                    'price',
                                    Number(e.target.value)
                                  )
                                }
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment
                                      position="start"
                                      sx={{ fontSize: '1.5rem' }}
                                    >
                                      <AttachMoney></AttachMoney>
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  flex: 1,
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    fontSize: '1.5rem',
                                    '&:hover': {
                                      '& > fieldset': {
                                        borderColor: 'primary.main',
                                      },
                                    },
                                  },
                                }}
                                size="small"
                              />

                              <FormControl size="small" sx={{ flex: 1 }}>
                                <Select
                                  value={pricing.duration}
                                  onChange={(e) =>
                                    updatePricing(
                                      index,
                                      'duration',
                                      e.target.value
                                    )
                                  }
                                  startAdornment={
                                    <InputAdornment position="start">
                                      <Schedule sx={{ fontSize: 12, ml: 1 }} />
                                    </InputAdornment>
                                  }
                                  sx={{ fontSize: '1.5rem' }}
                                >
                                  <MenuItem value="monthly">Monthly</MenuItem>
                                  <MenuItem value="yearly">Yearly</MenuItem>
                                </Select>
                              </FormControl>
                            </Stack>
                          </Paper>
                        </Fade>
                      ))}
                      {!!formError.pricings && (
                        <Typography variant="body2" color={'error'}>
                          {formError.pricings}
                        </Typography>
                      )}
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Features Section */}
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack spacing={3}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      width={'100%'}
                    >
                      <Star color="warning" />
                      <Typography variant="h4" fontWeight="600">
                        Plan Features
                      </Typography>
                      <Chip size="small" color="warning" variant="outlined" />
                    </Box>
                    <Button
                      startIcon={<Add />}
                      onClick={addFeature}
                      variant="contained"
                      size="small"
                      sx={{
                        borderRadius: 2,
                        width: 'fit-content',
                        fontSize: '1.5rem',
                        textTransform: 'none',
                        background:
                          'linear-gradient(45deg, #ff9800 30%, #ffb74d 90%)',
                        '&:hover': {
                          background:
                            'linear-gradient(45deg, #f57c00 30%, #ff9800 90%)',
                        },
                      }}
                    >
                      Add
                    </Button>
                  </Stack>

                  {features.length === 0 ? (
                    <Paper
                      sx={{
                        p: 6,
                        textAlign: 'center',
                        bgcolor: 'grey.50',
                        border: '2px dashed',
                        borderColor: 'grey.300',
                        borderRadius: 2,
                      }}
                    >
                      <Star sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                      <Typography variant="body1" color="text.secondary">
                        No features defined yet. Click "Add Feature" to start
                        building your feature list.
                      </Typography>
                      {!!formError?.features && (
                        <Typography variant="body2" color={'error'}>
                          *{formError?.features}
                        </Typography>
                      )}
                    </Paper>
                  ) : (
                    <Stack spacing={2}>
                      {features.map((feature, index) => (
                        <Fade in key={index}>
                          <Paper
                            elevation={1}
                            sx={{
                              p: 3,
                              borderRadius: 2,
                              background:
                                'linear-gradient(135deg, #fff8e1 0%, #fff3c4 100%)',
                              border: '1px solid',
                              borderColor: 'warning.light',
                            }}
                          >
                            <Stack spacing={2}>
                              <Stack
                                direction="row"
                                spacing={2}
                                alignItems="end"
                              >
                                <TextField
                                  value={feature.name}
                                  onChange={(e) =>
                                    updateFeature(index, 'name', e.target.value)
                                  }
                                  placeholder="e.g., API Calls, Storage"
                                  sx={{
                                    flex: 2,
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                      fontSize: '1.5rem',
                                      '&:hover': {
                                        '& > fieldset': {
                                          borderColor: 'primary.main',
                                        },
                                      },
                                    },
                                  }}
                                  size="small"
                                />

                                <TextField
                                  type="text"
                                  value={feature.max_limit || ''}
                                  onChange={(e) =>
                                    updateFeature(
                                      index,
                                      'max_limit',
                                      Number(e.target.value)
                                    )
                                  }
                                  placeholder="Add Max Limit"
                                  sx={{
                                    flex: 1,
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                      fontSize: '1.5rem',
                                      '&:hover': {
                                        '& > fieldset': {
                                          borderColor: 'primary.main',
                                        },
                                      },
                                    },
                                  }}
                                  size="small"
                                />

                                <IconButton
                                  color="error"
                                  onClick={() => removeFeature(feature?.id)}
                                  sx={{
                                    '&:hover': {
                                      transform: 'scale(1.1)',
                                      transition: 'transform 0.2s ease',
                                    },
                                  }}
                                >
                                  <Delete />
                                </IconButton>
                              </Stack>

                              <TextField
                                value={feature.description}
                                onChange={(e) =>
                                  updateFeature(
                                    index,
                                    'description',
                                    e.target.value
                                  )
                                }
                                placeholder="Brief description of this feature"
                                fullWidth
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    fontSize: '1.5rem',
                                    '&:hover': {
                                      '& > fieldset': {
                                        borderColor: 'primary.main',
                                      },
                                    },
                                  },
                                }}
                              />
                            </Stack>
                          </Paper>
                        </Fade>
                      ))}
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          bgcolor: 'grey.50',
          borderTop: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1.5rem',

            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          // disabled={!name}
          variant="contained"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1.5rem',
            px: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
            },
            '&:disabled': {
              background: 'grey.300',
            },
          }}
        >
          {initialData ? 'Update Plan' : 'Create Plan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
